import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    type CustomSurveySnapshot,
    type CustomSurveySummary,
    surveysApi,
    Survey,
} from '@/api/surveys';
import {
    ClipboardText,
    DocumentText,
    Send2,
    Edit2,
    Add,
    Trash,
    MoreVertical,
} from '@/components/icons';
import GoogleFormsIcon from '@/components/icons/GoogleFormsIcon';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AssignableResourceCard, CardGrid, EmptyState, SegmentedTabs } from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { defaultSnapshot, getNextCustomSurveyName } from '@/lib/custom-surveys/schema';
import { getCustomSurveyReadiness } from '@/lib/custom-surveys/status';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import WrapperLayout from '@/pages/components/WrapperLayout';
import { CustomSurveyBuilderPage } from '@/pages/constants';
import { Patient } from '@/types/patient';
import SendSurveyDialog from './components/SendSurveyDialog';
import SentSurveysTable from './components/SentSurveysTable';
import ImportGoogleFormsDialog from './custom-builder/components/ImportGoogleFormsDialog';

const publishedSurveyFromCustom = (survey: CustomSurveySummary): Survey | null => {
    if (!survey.published) return null;

    return {
        id: survey.id,
        name: survey.published.schema.name,
        instructions: survey.published.schema.instructions,
        source: 'custom',
        status: 'PUBLISHED',
        sections: survey.published.schema.sections,
    };
};

export default function SurveysPage() {
    const { openModal, openWizardModal, closeModal } = useModalContext();
    const confirmDelete = useConfirmDelete();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const sendSurveyAccess = useFeatureEntitlement('surveys.send');
    const customSurveyAccess = useFeatureEntitlement('surveys.custom.create');
    const [activeSurveyTab, setActiveSurveyTab] = useState('platform');
    const preSelectedPatient = location.state?.preSelectedPatient as Patient | undefined;

    const { data: surveys = [], isLoading } = useQuery({
        queryKey: ['surveys'],
        queryFn: surveysApi.list,
    });

    const { data: customSurveys = [], isLoading: isLoadingCustomSurveys } = useQuery({
        queryKey: ['surveys', 'custom'],
        queryFn: surveysApi.listCustom,
    });

    const createCustomMutation = useMutation({
        mutationFn: (snapshot?: CustomSurveySnapshot) =>
            surveysApi.createCustom(
                snapshot ?? defaultSnapshot(getNextCustomSurveyName(customSurveys)),
            ),
        onSuccess: async (survey) => {
            await queryClient.invalidateQueries({ queryKey: ['surveys'] });
            navigate(
                `/${generatePath(CustomSurveyBuilderPage, {
                    surveyId: survey.id,
                })}`,
            );
        },
        onError: (error) => {
            const status =
                error && typeof error === 'object' && 'response' in error
                    ? (error.response as { status?: number } | undefined)?.status
                    : undefined;

            toast.error(
                status === 409
                    ? 'Já existe um formulário com esse título. Renomeie o existente ou tente novamente.'
                    : 'Não foi possível criar o formulário.',
            );
        },
    });

    const deleteCustomMutation = useMutation({
        mutationFn: surveysApi.deleteCustom,
        onSuccess: async (impact) => {
            toast.success(
                `Formulário excluído. ${impact.pendingAssignmentsExpired} link(s) pendente(s) expirado(s).`,
            );
            await queryClient.invalidateQueries({ queryKey: ['surveys'] });
        },
        onError: () => {
            toast.error('Não foi possível excluir o formulário.');
        },
    });

    const platformSurveys = surveys.filter((survey) => survey.source !== 'custom');

    const handleSendClick = (survey: Survey) => {
        if (sendSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendSurveyAccess.decision));
            return;
        }

        openWizardModal({
            content: <SendSurveyDialog survey={survey} preSelectedPatient={preSelectedPatient} />,
            size: 'md',
        });
    };

    const handlePreviewClick = (survey: Survey) => {
        navigate(`/surveys/${survey.id}/preview`);
    };

    const handleCreateCustomSurvey = () => {
        if (customSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(customSurveyAccess.decision));
            return;
        }

        createCustomMutation.mutate(undefined);
    };

    const handleImportGoogleForms = () => {
        if (customSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(customSurveyAccess.decision));
            return;
        }

        openModal({
            title: 'Importar do Google Forms',
            size: 'md',
            content: (
                <ImportGoogleFormsDialog
                    hasContent={false}
                    onClose={closeModal}
                    onConfirm={(snapshot) => {
                        closeModal();
                        createCustomMutation.mutate(snapshot);
                    }}
                />
            ),
        });
    };

    const handleEditCustomSurvey = (surveyId: string) => {
        navigate(
            `/${generatePath(CustomSurveyBuilderPage, {
                surveyId,
            })}`,
        );
    };

    const handleDeleteCustomSurvey = (survey: CustomSurveySummary) => {
        confirmDelete({
            title: 'Excluir formulário?',
            description: `Você está prestes a excluir "${survey.name}". Links pendentes serão expirados e respostas já concluídas continuarão acessíveis. Essa ação não poderá ser desfeita.`,
            onConfirm: (close) => {
                deleteCustomMutation.mutate(survey.id);
                close();
            },
        });
    };

    const renderSurveyCard = (survey: Survey) => (
        <AssignableResourceCard
            key={survey.id}
            title={survey.name}
            badges={
                survey.source === 'custom' ? <Badge variant="info">Meu formulário</Badge> : null
            }
            primaryAction={{
                label: 'Enviar',
                icon: Send2,
                onClick: () => handleSendClick(survey),
                ariaDisabled: sendSurveyAccess.shouldBlock,
                accessory:
                    sendSurveyAccess.isUnavailableInPlan && sendSurveyAccess.availableFromPlan ? (
                        <FeatureUnavailableChip
                            availablePlan={sendSurveyAccess.availableFromPlan}
                        />
                    ) : null,
            }}
            secondaryAction={{
                label: 'Visualizar',
                icon: DocumentText,
                onClick: () => handlePreviewClick(survey),
            }}
        />
    );

    return (
        <WrapperLayout title="Formulários" leftIcon={ClipboardText}>
            {isLoading || isLoadingCustomSurveys ? (
                <Loading />
            ) : (
                <SegmentedTabs
                    value={activeSurveyTab}
                    onValueChange={setActiveSurveyTab}
                    headerAction={
                        activeSurveyTab === 'custom' ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button disabled={createCustomMutation.isPending}>
                                        <Add className="size-4" />
                                        Criar formulário
                                        {customSurveyAccess.isUnavailableInPlan &&
                                        customSurveyAccess.availableFromPlan ? (
                                            <FeatureUnavailableChip
                                                availablePlan={customSurveyAccess.availableFromPlan}
                                            />
                                        ) : null}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onClick={handleCreateCustomSurvey}
                                        className="cursor-pointer"
                                    >
                                        <Add className="mr-2 size-4" />
                                        Criar manualmente
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={handleImportGoogleForms}
                                        className="cursor-pointer"
                                    >
                                        <GoogleFormsIcon className="mr-2 size-4" />
                                        Importar do Google Forms
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : null
                    }
                    tabs={[
                        {
                            value: 'platform',
                            label: 'Formulários Laudopsi',
                            content:
                                platformSurveys.length === 0 ? (
                                    <EmptyState
                                        icon={ClipboardText}
                                        title="Nenhum formulário Laudopsi disponível"
                                        description="Quando houver formulários ativos, eles aparecerão aqui."
                                    />
                                ) : (
                                    <CardGrid>{platformSurveys.map(renderSurveyCard)}</CardGrid>
                                ),
                        },
                        {
                            value: 'custom',
                            label: 'Meus formulários',
                            content: (
                                <div>
                                    {customSurveys.length === 0 ? (
                                        <EmptyState
                                            icon={DocumentText}
                                            title="Nenhum formulário personalizado"
                                        />
                                    ) : (
                                        <CardGrid>
                                            {customSurveys.map((customSurvey) => {
                                                const publishedSurvey =
                                                    publishedSurveyFromCustom(customSurvey);
                                                const readiness =
                                                    getCustomSurveyReadiness(customSurvey);

                                                return (
                                                    <AssignableResourceCard
                                                        key={customSurvey.id}
                                                        title={customSurvey.name}
                                                        badges={
                                                            <Badge variant={readiness.badgeVariant}>
                                                                {readiness.badgeLabel}
                                                            </Badge>
                                                        }
                                                        meta={
                                                            readiness.hint ? (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {readiness.hint}
                                                                </p>
                                                            ) : undefined
                                                        }
                                                        headerAction={
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        aria-label={`Ações de ${customSurvey.name}`}
                                                                        className="size-9 shrink-0 text-muted-foreground hover:text-foreground"
                                                                    >
                                                                        <MoreVertical className="size-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            handleEditCustomSurvey(
                                                                                customSurvey.id,
                                                                            )
                                                                        }
                                                                        className="cursor-pointer"
                                                                    >
                                                                        <Edit2 className="mr-2 size-4" />
                                                                        Editar
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            handleDeleteCustomSurvey(
                                                                                customSurvey,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            deleteCustomMutation.isPending
                                                                        }
                                                                        className="cursor-pointer text-red-600 focus:text-red-600"
                                                                    >
                                                                        <Trash className="mr-2 size-4" />
                                                                        Excluir
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        }
                                                        primaryAction={
                                                            publishedSurvey
                                                                ? {
                                                                      label: 'Enviar',
                                                                      icon: Send2,
                                                                      onClick: () =>
                                                                          handleSendClick(
                                                                              publishedSurvey,
                                                                          ),
                                                                      ariaDisabled:
                                                                          sendSurveyAccess.shouldBlock,
                                                                      accessory:
                                                                          sendSurveyAccess.isUnavailableInPlan &&
                                                                          sendSurveyAccess.availableFromPlan ? (
                                                                              <FeatureUnavailableChip
                                                                                  availablePlan={
                                                                                      sendSurveyAccess.availableFromPlan
                                                                                  }
                                                                              />
                                                                          ) : null,
                                                                  }
                                                                : {
                                                                      label: 'Enviar',
                                                                      icon: Send2,
                                                                      ariaDisabled: true,
                                                                      tooltip:
                                                                          'Publique o formulário para enviar.',
                                                                      onClick: () =>
                                                                          toast.warning(
                                                                              'Publique o formulário antes de enviar.',
                                                                          ),
                                                                  }
                                                        }
                                                        secondaryAction={
                                                            publishedSurvey
                                                                ? {
                                                                      label: 'Visualizar',
                                                                      icon: DocumentText,
                                                                      onClick: () =>
                                                                          handlePreviewClick(
                                                                              publishedSurvey,
                                                                          ),
                                                                  }
                                                                : undefined
                                                        }
                                                    />
                                                );
                                            })}
                                        </CardGrid>
                                    )}
                                </div>
                            ),
                        },
                    ]}
                />
            )}

            <div className="mt-6 sm:mt-12">
                <SentSurveysTable />
            </div>
        </WrapperLayout>
    );
}
